import { useSearchVideosMutation } from "@api/media";
import { MediaLibFileCheckbox } from "@editor/modules/media-lib/components/files/file";
import {
  type ReactNode,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ListRowProps } from "react-virtualized";
import { useIntersectionObserver } from "usehooks-ts";
import {
  VIDEOS_PANEL_STOCK_MASONRY_GAPS,
  VIDEOS_PANEL_STOCK_QUERY_KEYS,
} from "../../constants";
import { useStockVideosCtx } from "../../ctx";
import type { IStockVideo } from "../../types";
import { makeVideosPanelVideoElement } from "../../utils";
import { StockVideoSkeleton } from "../stock-video-skeleton";

interface IProps {
  rowData: ListRowProps;
  videoData?: IStockVideo | null;
}

export const StockMasonryRow = ({ rowData: { style }, videoData }: IProps) => {
  const [, { isLoading: isFetchingVideos }] = useSearchVideosMutation({
    fixedCacheKey: VIDEOS_PANEL_STOCK_QUERY_KEYS.VIDEOS,
  });

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
  });

  const { isMultiSelect, onSelect, selectedItems, view } = useStockVideosCtx();

  const isSelected = useMemo(
    () => !!selectedItems?.find((el) => el.id === videoData?.url),
    [selectedItems, videoData?.url],
  );

  const [isOver, setIsOver] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const height = (style.height as number) - VIDEOS_PANEL_STOCK_MASONRY_GAPS;
  if (!videoData) {
    return (
      <div
        style={{
          ...style,
          height,
          opacity: isFetchingVideos ? 1 : 0,
        }}
      >
        <StockVideoSkeleton />
      </div>
    );
  }

  const handleMouseEnter = () => {
    setIsOver(true);
    hoverTimeoutRef.current = setTimeout(() => {
      setShouldLoadVideo(true);
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    }, 250);
  };

  const handleMouseLeave = () => {
    setIsOver(false);
    setShouldLoadVideo(false);
    setIsLoaded(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <Wrapper videoData={videoData}>
      <div
        onClick={() => {
          if (view === "dialog") {
            onSelect?.(videoData);
          }
        }}
        ref={ref}
        className="tw-rounded tw-bg-blue-50 tw-outline-none tw-outline-1 tw-outline-offset-1 tw-transition-colors hover:tw-outline-primary"
        style={{
          ...style,
          height,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="tw-relative tw-size-full">
          {isMultiSelect && <MediaLibFileCheckbox isSelected={isSelected} />}

          {isIntersecting && (
            <img
              src={videoData.thumbnailImage}
              alt=""
              role="presentation"
              className="tw-size-full tw-rounded tw-object-cover tw-opacity-0 tw-transition-opacity tw-duration-200"
              onLoad={(e) => {
                const img = e.target as HTMLImageElement;
                img.classList.remove("tw-opacity-0");
                img.classList.add("tw-opacity-100");
              }}
            />
          )}
          {shouldLoadVideo && isIntersecting && (
            <video
              ref={videoRef}
              key={videoData.id}
              onLoadStart={() => {
                setIsLoaded(false);
              }}
              onLoadedData={() => {
                setIsLoaded(true);
              }}
              className={`tw-absolute tw-left-0 tw-top-0 tw-z-2 tw-size-full tw-rounded tw-object-cover tw-transition-opacity tw-duration-200 ${
                isLoaded && isOver ? "tw-opacity-100" : "tw-opacity-0"
              }`}
              muted
              playsInline
              loop
              autoPlay
            >
              <source src={videoData.thumbnailVideo} type="video/mp4" />
            </video>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

const Wrapper = memo(
  ({
    children,
    videoData,
  }: {
    children: ReactNode;
    videoData: IStockVideo;
  }) => {
    const { elementWrapper } = useStockVideosCtx();
    if (!elementWrapper) return <>{children}</>;
    const ElementWrapper = elementWrapper;

    return (
      <ElementWrapper
        element={makeVideosPanelVideoElement({
          src: videoData.url,
          type: "Library",
        })}
      >
        {children}
      </ElementWrapper>
    );
  },
);
